/**
 * CloudConfig — parameters for the volumetric cloud subsystem.
 *
 * Based on Schneider 2015 "Real-time Volumetric Cloudscapes of Horizon Zero
 * Dawn" with Wrenninge 2015 inspired multi-scatter simplification.
 *
 * v1 scope:
 *   - Single cumulus-like layer between bottomAltitude and topAltitude (m)
 *   - Pre-baked 64³ 3D noise (Perlin-ish base + Worley-ish detail)
 *   - Dual-lobe Henyey-Greenstein phase function
 *   - Beer-Powder self-shadow via 4-sample sun cone
 *   - Full-resolution raymarch (no temporal reconstruction in v1)
 *
 * v2 roadmap:
 *   - 128³ noise with full Schneider density function + per-type height gradient
 *   - 1/4 checkerboard + motion-vector temporal reconstruction
 *   - Wrenninge multi-scatter (N-octave Beer's law)
 *   - Aerial-perspective LUT integration (needs Hillaire sky v2)
 *
 * All distances are meters.
 */

export interface CloudQualityPreset {
  /** Number of primary view-ray raymarch steps. More = less banding. */
  primarySamples: number;
  /** Number of sun-visibility cone samples per main sample. */
  sunSamples: number;
  /** Linear step size inside a cloud (m). */
  stepInside: number;
  /** Linear step size outside clouds (m). */
  stepOutside: number;
}

export const CLOUD_PRESETS: Record<'low' | 'medium' | 'high' | 'ultra', CloudQualityPreset> = {
  low:    { primarySamples: 24, sunSamples: 2, stepInside: 40, stepOutside: 150 },
  medium: { primarySamples: 48, sunSamples: 4, stepInside: 25, stepOutside: 100 },
  high:   { primarySamples: 64, sunSamples: 6, stepInside: 15, stepOutside: 80 },
  ultra:  { primarySamples: 96, sunSamples: 6, stepInside: 10, stepOutside: 50 },
};

export interface CloudConfig {
  /** Bottom altitude of the cloud layer above ground (meters). Default 1500. */
  bottomAltitude: number;
  /** Top altitude of the cloud layer above ground (meters). Default 4000. */
  topAltitude: number;
  /** Extinction coefficient (1/m) — how rapidly light is absorbed. Default 0.05. */
  extinction: number;
  /** Global cloud coverage 0..1. Can be driven by WeatherSystem. Default 0.5. */
  coverage: number;
  /** Cloud density multiplier (base density scale). Default 1.0. */
  densityScale: number;
  /** Wind vector (m/s) that scrolls the noise volumes. Default (0,0). */
  windX: number;
  windZ: number;
  /**
   * Time multiplier on wind accumulation. Default 1 (physically-scaled).
   * Real cloud masses move at real wind speed, which from the ground reads as
   * STATIONARY in game time — at 6 m/s the weather field (the macro cloud
   * shapes, 1/0.00002 m tile) drifts one tile per ~2.3 h. Values of 5-15 give
   * the cinematic "sky is alive" drift games actually want.
   */
  windTimeScale: number;
  /** Sun illuminance multiplier used when lighting clouds. Default 5. */
  sunIlluminance: number;
  /** Ambient term for bottom of the cloud (sky-scattered fallback). Default 0.3. */
  ambientBottom: number;
  /** Ambient term for top of the cloud (near-sun fallback). Default 1.0. */
  ambientTop: number;
  /** HG forward-scatter parameter. Default 0.8. */
  phaseForwardG: number;
  /** HG backward-scatter parameter. Default -0.5. */
  phaseBackwardG: number;
  /** Powder-effect strength (0..1). Default 0.35. */
  powderStrength: number;
  /**
   * Noise sampling scale — UV scale per meter for the 3D volume. Default
   * 0.0004: one noise period spans ~2.5 km of world space, so the base shape
   * octave reads as ~1.2 km soft cloud masses rather than the busy ~600 m
   * repeating puffs the old 0.0008 produced. Lower = larger, softer masses.
   */
  noiseScale: number;
  /**
   * Weather/coverage map sampling scale — UV per meter for the 2D weather
   * texture. Default 0.0001: one weather tile spans 10 km, so coverage
   * masses land ~2-6 km apart and decks regularly sit OVERHEAD. The old
   * hardwired 0.00002 (50 km tile) carved the sky into vast all-or-nothing
   * cells — a player inside a clear cell saw every cloud 10-40 km away,
   * pinned at 2-5° elevation on the horizon ("the clouds are never above
   * me"), and walking never changed the weather.
   */
  weatherScale: number;
  /**
   * Camera-translation response of the cloud pattern.
   *   0        — CAMERA rendering space (Unity HDRP "Rendering Space:
   *              Camera", the classic skybox-cloud convention): the pattern
   *              is a pure function of view direction + wind — the sky never
   *              slides against the world, but the SAME cloud arrangement
   *              travels with the player forever.
   *   ~0.1-0.2 — TRAVEL EVOLUTION (2026-08-18 experiment, reverted
   *              2026-08-19): imperceptible per step, a multi-km journey
   *              arrives under a different sky — but at walking scale it is
   *              perceptually identical to camera-glued ("the clouds follow
   *              me" persisted). Kept as a knob, not recommended.
   *   1        — physically correct WORLD anchoring (atlas default): clouds
   *              are stationary in the world; the low layer carries
   *              walk-speed parallax legibility. Field lesson: k>1 swims
   *              against architecture — never ship exaggerated.
   *   >1       — exaggerated parallax stylization.
   * Rotation anchoring, wind, the sun cone, and vertical motion are correct
   * at every value; the shift is rebase-continuous.
   */
  parallaxScale: number;
  /**
   * Net-zero shape evolution speed (m/s in pattern space). The base noise
   * drifts +v while the detail-erosion noise counter-drifts −v, so cloud
   * shapes continuously morph IN PLACE with zero average translation (the
   * efficient-volumetric-clouds "net 0 drift" technique) — the sky reads as
   * alive even for a stationary player, without any second wind layer.
   * 0 disables. Default 1.5.
   */
  evolveSpeed: number;
  /** Quality preset (also controls step counts). */
  quality: CloudQualityPreset;
}

export function createCloudConfig(overrides: Partial<CloudConfig> = {}): CloudConfig {
  return {
    bottomAltitude: overrides.bottomAltitude ?? 1500,
    topAltitude: overrides.topAltitude ?? 4000,
    extinction: overrides.extinction ?? 0.05,
    coverage: overrides.coverage ?? 0.5,
    densityScale: overrides.densityScale ?? 1.0,
    windX: overrides.windX ?? 0,
    windZ: overrides.windZ ?? 0,
    windTimeScale: overrides.windTimeScale ?? 1,
    sunIlluminance: overrides.sunIlluminance ?? 5,
    ambientBottom: overrides.ambientBottom ?? 0.3,
    ambientTop: overrides.ambientTop ?? 1.0,
    phaseForwardG: overrides.phaseForwardG ?? 0.8,
    phaseBackwardG: overrides.phaseBackwardG ?? -0.5,
    powderStrength: overrides.powderStrength ?? 0.35,
    noiseScale: overrides.noiseScale ?? 0.0004,
    weatherScale: overrides.weatherScale ?? 0.0001,
    parallaxScale: overrides.parallaxScale ?? 1,
    evolveSpeed: overrides.evolveSpeed ?? 1.5,
    quality: overrides.quality ?? CLOUD_PRESETS.medium,
  };
}

// ─── CPU reference helpers (tested) ─────────────────────────────────────────

/**
 * Henyey-Greenstein phase function.
 *
 *   p(µ, g) = (1/4π) * (1 - g²) / (1 + g² - 2g·µ)^(3/2)
 *
 * - g = 0    → isotropic
 * - g > 0    → forward-scattering (sun halo)
 * - g < 0    → backward-scattering (silver lining)
 *
 * Renamed from `henyeyGreenstein` to `cloudPhaseHG` to avoid collision
 * with the identical function exported from the GI (GodRays) subsystem.
 */
export function cloudPhaseHG(cosTheta: number, g: number): number {
  const g2 = g * g;
  const denom = Math.pow(Math.max(1e-6, 1 + g2 - 2 * g * cosTheta), 1.5);
  return (1.0 - g2) / (4.0 * Math.PI * denom);
}

/**
 * Dual-lobe Henyey-Greenstein — equal mix of forward + backward phase functions.
 * Schneider's published default pair is (gF=0.8, gB=-0.5).
 */
export function dualLobeHG(cosTheta: number, gF: number, gB: number): number {
  return 0.5 * (cloudPhaseHG(cosTheta, gF) + cloudPhaseHG(cosTheta, gB));
}

/**
 * Beer's law: transmittance after traveling optical depth `od`.
 *   T = exp(-od)
 */
export function beer(opticalDepth: number): number {
  return Math.exp(-opticalDepth);
}

/**
 * Powder effect — Schneider's fix for the silver-lining-only look.
 * Dark-side attenuation that self-cancels for very thick clouds.
 *   P = 1 - exp(-2·od)
 */
export function powder(opticalDepth: number): number {
  return 1.0 - Math.exp(-2.0 * opticalDepth);
}

/**
 * Normalized height gradient for the cloud layer (0 at bottom, 1 at top).
 * A simple cumulus-shape bell curve: peaks around h=0.5 with smooth
 * taper-off at both ends. Good enough for v1; v2 would blend three
 * per-type gradients (stratus / stratocumulus / cumulus).
 */
export function cumulusHeightGradient(h: number): number {
  if (h <= 0 || h >= 1) return 0;
  // Symmetric bell: 4h(1-h) peaks at 0.5 with value 1.
  return 4.0 * h * (1.0 - h);
}

/**
 * Remap x from [a, b] to [c, d], clamping to the output range.
 * This is the "remap" primitive from the Schneider talks — used inside
 * both the density function and the coverage attenuation.
 */
export function remapClamped(
  x: number, a: number, b: number, c: number, d: number,
): number {
  if (b === a) return c;
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return c + t * (d - c);
}
